import { useAppDispatch } from 'app/hooks';
import axios from 'axios';
import AutoCompleteSelect from 'components/Form/autoCompleteSelect';
import { AutoCompleteOption } from 'components/Form/autoCompleteSelect/autoComponetSelect.interface';
import AirportInterface from 'models/flight/airport.interface';
import { Maybe } from 'models/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDeparturAirportService } from 'services/publicData.service';
import { setDepartureError, setDepartureLoading, setDepartureLocationsRoute } from 'store/publicData/publicDataSlice';
import urls from 'utils/urls';
import { callbackPropsDepartureArrivalField } from './departureArrivalField.interface';
import * as S from './departureArrivalField.styles';
interface AirportLocations extends AirportInterface, AutoCompleteOption {
  show?: boolean;
}
interface DepartureArrivalFieldProps {
  defaultDeparture?: string;
  defaultArrival?: string;
  onChange: (callbackProps: callbackPropsDepartureArrivalField) => void;
}

const DepartureArrivalField = ({ defaultDeparture, defaultArrival, onChange }: DepartureArrivalFieldProps) => {
  const [selectedDeparture, setSelectedDeparture] = useState<Maybe<AirportInterface>>(null);
  const [selectedArrival, setSelectedArrival] = useState<Maybe<AirportInterface>>(null);

  const [allDepartureLocations, setAllDepartureLocations] = useState<AirportLocations[]>([]);
  const [departureLocations, setDepartureLocations] = useState<AirportLocations[]>([]);
  const [allArrivalLocations, setAllArrivalLocations] = useState<AirportLocations[]>([]);
  const [arrivalLocations, setArrivalLocations] = useState<AirportLocations[]>([]);
  const [show, setShow] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const [defaultDestinationIsSet, setDefaultDestinationIsSet] = useState<boolean>(false);
  const { t } = useTranslation();
  const setDestinations = (data: AirportLocations[] = []) => {
    setAllArrivalLocations(data);
    setArrivalLocations(data);
  };

  const setDepartures = (data: AirportLocations[] = []) => {
    setAllDepartureLocations(data);
    setDepartureLocations(data);
  };

  const getDepartureLocations = async () => {
    dispatch(setDepartureLoading());

    getDeparturAirportService()
      .then((res) => {
        dispatch(setDepartureLocationsRoute(res.data));

        if (res.data) {
          const preparedList = prepareList(res.data);
          setDepartures(preparedList);
          if (defaultDeparture) getDestinationLocations(defaultDeparture);

          setShow(true);
        } else {
          setDepartures();
          setShow(true);
        }
      })
      .catch((err) => {
        dispatch(setDepartureError({ err }));
      });

    // const res = await axios.get(urls.departureRoute);
    // if (res) {
    //   const response = res.data.response as AirportInterface[];
    //   const preparedList = prepareList(response);
    //   setDepartures(preparedList);
    //   if (defaultDeparture) await getDestinationLocations(defaultDeparture);

    //   setShow(true);
    // } else {
    //   setDepartures();
    //   setShow(true);
    // }
  };

  const getDestinationLocations = async (departureCode: string) => {
    const res = await axios.get(urls.arrivalLocationsRoute + `?departure=${departureCode}`);
    if (res) {
      const response = prepareList(res.data.response as AirportInterface[]);
      setDestinations(response);
      if (!defaultDestinationIsSet) {
        if (defaultArrival) {
          const arrivalItem = response.find((e) => e.id === defaultArrival);
          if (arrivalItem) {
            setSelectedArrival(arrivalItem);
          }
        }

        setDefaultDestinationIsSet(true);
      }
    } else {
      setDestinations();
    }
  };

  const prepareList = (data: AirportInterface[]): AirportLocations[] => {
    const preparedList = [] as AirportLocations[];
    for (const i in data) {
      const airport = data[i];
      preparedList.push({ ...airport, id: airport.code, name: `${airport.name} (${airport.code})` });
    }
    return preparedList;
  };

  useEffect(() => {
    getDepartureLocations();
  }, []);

  useEffect(() => {
    if (defaultDeparture) {
      const departureItem = departureLocations.find((e) => e.id === defaultDeparture);

      if (departureItem) {
        setSelectedDeparture(departureItem);
      }
    }
  }, [defaultDeparture, departureLocations]);
  // useEffect(() => {
  //   if (defaultArrival) {
  //     const arrivalItem = arrivalLocations.find((e) => e.id === defaultArrival);

  //     if (arrivalItem) {
  //       setSelectedArrival(arrivalItem);
  //     }
  //   }
  // }, [defaultArrival, arrivalLocations]);

  useEffect(() => {
    if (selectedDeparture) {
      getDestinationLocations(selectedDeparture.code);
    } else {
      setDestinations();
    }
  }, [selectedDeparture]);

  return (
    (show && (
      <S.FieldsRow>
        <S.Field>
          <AutoCompleteSelect
            options={departureLocations}
            label={t('pages.services.flight.pages.form.fields.from.label')}
            defaultValue={defaultDeparture}
            getOptions={(value) => {
              setDepartureLocations(
                [...allDepartureLocations].filter((e) =>
                  (e.name + '  ' + e.countryCode).toLowerCase().includes(value.toLowerCase())
                )
              );
            }}
            onChange={(value) => {
              setSelectedDeparture(value);
              //TODO: empty arrival field if departure is changed
              // setSelectedArrival(null);
              onChange({ departure: value, arrival: selectedArrival });
            }}
          />
        </S.Field>
        <S.Field>
          <AutoCompleteSelect
            options={arrivalLocations}
            getOptions={(value) => {
              setArrivalLocations(
                [...allArrivalLocations].filter((e) =>
                  (e.name + '  ' + e.countryCode).toLowerCase().includes(value.toLowerCase())
                )
              );
            }}
            defaultValue={selectedArrival?.code}
            mustSetDefaultValue={Boolean(defaultDeparture)}
            label={t('pages.services.flight.pages.form.fields.to.label')}
            onChange={(value) => {
              setSelectedArrival(value);
              onChange({ departure: selectedDeparture, arrival: value });
            }}
          />
        </S.Field>
      </S.FieldsRow>
    )) || <></>
  );
};

export default DepartureArrivalField;
